import React, { useState } from 'react';
import { Clock, ArrowRight } from 'lucide-react';

import { formatNewsTime } from '../../utils/newsMapper';

export const NewsCard = ({ article }) => {
  const { category, gradient, title, snippet, source, publishedAt, tag, url, image } = article;
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = image && !imageFailed;
  const time = formatNewsTime(publishedAt);

  // External stories open in a new tab
  const linkProps = url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {};

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs hover:border-indigo-300 hover:shadow-card transition-all duration-200 flex flex-col justify-between overflow-hidden group">
      <div>
        {/* Header Banner: article image, or a category gradient when there isn't one */}
        <div
          className={`h-24 sm:h-28 bg-gradient-to-tr ${gradient} p-3.5 sm:p-4 text-white flex flex-col justify-between relative overflow-hidden`}
        >
          {showImage && (
            <img
              src={image}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setImageFailed(true)}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          {/* Overlay keeps the badges readable on photos */}
          <div className={`absolute inset-0 ${showImage ? 'bg-gradient-to-b from-black/50 via-black/10 to-black/40' : 'bg-black/10'}`} />

          <div className="relative flex items-center justify-between z-10">
            <span className="text-[10px] font-extrabold uppercase tracking-wider bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded text-white/95">
              {category}
            </span>
            {time && (
              <span className="text-[11px] font-medium text-white/90 flex items-center gap-1 bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded">
                <Clock className="w-3 h-3" />
                <span>{time}</span>
              </span>
            )}
          </div>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-5 space-y-2.5">
          <div className="text-[11px] text-slate-400 font-medium truncate">{source}</div>

          <h3 className="font-bold text-sm sm:text-base text-slate-900 group-hover:text-indigo-600 transition-colors leading-snug line-clamp-3">
            {url ? <a {...linkProps}>{title}</a> : title}
          </h3>

          {snippet && (
            <p className="text-xs text-slate-500 font-normal leading-relaxed line-clamp-3">
              {snippet}
            </p>
          )}
        </div>
      </div>

      {/* Card Footer: Amount Tag + Read Link */}
      <div className="px-4 sm:px-5 pb-4 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
        {tag ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wide bg-emerald-50 text-emerald-700 border-emerald-200">
            {tag}
          </span>
        ) : (
          <span />
        )}

        {url && (
          <a
            {...linkProps}
            className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 group-hover:translate-x-0.5 transition-all"
          >
            <span>Read Story</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </div>
  );
};

export default NewsCard;
